import React, { useState } from 'react';
import { useComparison } from '../contexts/ComparisonContext';
import './ComparisonCartWidget.css';

interface ComparisonCartWidgetProps {
  onOpenComparison: (type: 'country' | 'company' | 'job') => void;
  onCompanyClick?: (company: any) => void;
  onJobClick?: (job: any) => void;
}

type TabType = 'countries' | 'companies' | 'jobs';

const ComparisonCartWidget: React.FC<ComparisonCartWidgetProps> = ({ onOpenComparison, onCompanyClick, onJobClick }) => {
  const { state, getComparisonCount, clearComparison, removeFromComparison } = useComparison();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('countries');
  
  const counts = getComparisonCount();
  const hasItems = counts.total > 0;
  
  // Debug logging
  // console.log('[ComparisonCartWidget] State:', state);
  // console.log('[ComparisonCartWidget] Counts:', counts);
  
  // Auto-switch to tab with new items
  React.useEffect(() => {
    if (counts.countries > 0 && activeTab !== 'countries' && counts.companies === 0 && counts.jobs === 0) {
      setActiveTab('countries');
    } else if (counts.companies > 0 && activeTab !== 'companies' && counts.countries === 0 && counts.jobs === 0) {
      setActiveTab('companies');
    } else if (counts.jobs > 0 && activeTab !== 'jobs' && counts.countries === 0 && counts.companies === 0) {
      setActiveTab('jobs');
    }
  }, [counts, activeTab]);
  
  if (!hasItems) return null;
  
  return (
    <div className={`comparison-cart-widget ${isExpanded ? 'expanded' : ''}`}>
      <button 
        className="cart-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label="Toggle comparison cart"
      >
        <span className="material-icons">compare</span>
        {counts.total > 0 && (
          <span className="cart-badge">{counts.total}</span>
        )}
      </button>
      
      {isExpanded && (
        <div className="cart-content">
          <div className="cart-header">
            <h3>Comparison Manager</h3>
            <button 
              className="close-btn"
              onClick={() => setIsExpanded(false)}
              aria-label="Close cart"
            >
              <span className="material-icons">close</span>
            </button>
          </div>
          
          <div className="cart-tabs">
            <button 
              className={`tab-btn ${activeTab === 'countries' ? 'active' : ''}`}
              onClick={() => setActiveTab('countries')}
            >
              <span className="material-icons">public</span>
              Countries
              {counts.countries > 0 && <span className="tab-badge">{counts.countries}</span>}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'companies' ? 'active' : ''}`}
              onClick={() => setActiveTab('companies')}
            >
              <span className="material-icons">business</span>
              Companies
              {counts.companies > 0 && <span className="tab-badge">{counts.companies}</span>}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
              onClick={() => setActiveTab('jobs')}
            >
              <span className="material-icons">work</span>
              Jobs
              {counts.jobs > 0 && <span className="tab-badge">{counts.jobs}</span>}
            </button>
          </div>
          
          <div className="cart-items">
            {activeTab === 'countries' && (
              <div className="cart-section active">
                <div className="section-header">
                  <h4>Selected Countries</h4>
                  {counts.countries > 0 && (
                    <button 
                      className="clear-btn"
                      onClick={() => clearComparison('country')}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                {counts.countries === 0 ? (
                  <div className="empty-section">
                    <span className="material-icons">public</span>
                    <p>No countries selected</p>
                    <small>Browse the Salaries page to add countries</small>
                  </div>
                ) : (
                  <>
                    <div className="item-list">
                      {state.countries.map(country => (
                        <div key={country.code} className="cart-item">
                          <div className="item-info">
                            <span className="item-flag">{country.flag}</span>
                            <div className="item-details">
                              <span className="item-name">{country.name}</span>
                              <span className="item-meta">{country.avgSalaryRange}</span>
                            </div>
                          </div>
                          <button 
                            className="remove-item-btn"
                            onClick={() => removeFromComparison('country', country.code)}
                            aria-label={`Remove ${country.name}`}
                          >
                            <span className="material-icons">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="compare-btn"
                      onClick={() => onOpenComparison('country')}
                      disabled={counts.countries < 2}
                    >
                      <span className="material-icons">compare_arrows</span>
                      Compare Countries
                    </button>
                  </>
                )}
              </div>
            )}
            
            {activeTab === 'companies' && (
              <div className="cart-section active">
                <div className="section-header">
                  <h4>Selected Companies</h4>
                  {counts.companies > 0 && (
                    <button 
                      className="clear-btn"
                      onClick={() => clearComparison('company')}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                {counts.companies === 0 ? (
                  <div className="empty-section">
                    <span className="material-icons">business</span>
                    <p>No companies selected</p>
                    <small>Browse the Companies page to add companies</small>
                  </div>
                ) : (
                  <>
                    <div className="item-list">
                      {state.companies.map(company => (
                        <div key={company.id} className="cart-item clickable" onClick={() => onCompanyClick && onCompanyClick(company)}>
                          <div className="item-info">
                            <div className="item-details">
                              <span className="item-name">{company.name}</span>
                              <span className="item-meta">{company.industry} • ★ {company.globalRating.toFixed(1)}</span>
                            </div>
                          </div>
                          <button 
                            className="remove-item-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromComparison('company', company.id);
                            }}
                            aria-label={`Remove ${company.name}`}
                          >
                            <span className="material-icons">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="compare-btn"
                      onClick={() => onOpenComparison('company')}
                      disabled={counts.companies < 2}
                    >
                      <span className="material-icons">compare_arrows</span>
                      Compare Companies
                    </button>
                  </>
                )}
              </div>
            )}
            
            {activeTab === 'jobs' && (
              <div className="cart-section active">
                <div className="section-header">
                  <h4>Selected Jobs</h4>
                  {counts.jobs > 0 && (
                    <button 
                      className="clear-btn"
                      onClick={() => clearComparison('job')}
                    >
                      Clear All
                    </button>
                  )}
                </div>
                {counts.jobs === 0 ? (
                  <div className="empty-section">
                    <span className="material-icons">work</span>
                    <p>No jobs selected</p>
                    <small>Browse the Jobs page to add positions</small>
                  </div>
                ) : (
                  <>
                    <div className="item-list">
                      {state.jobs.map(job => (
                        <div key={job.id} className="cart-item clickable" onClick={() => onJobClick && onJobClick(job)}>
                          <div className="item-info">
                            <div className="item-details">
                              <span className="item-name">{job.title}</span>
                              <span className="item-meta">{job.company} • {job.location}</span>
                            </div>
                          </div>
                          <button 
                            className="remove-item-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFromComparison('job', job.id);
                            }}
                            aria-label={`Remove ${job.title}`}
                          >
                            <span className="material-icons">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button 
                      className="compare-btn"
                      onClick={() => onOpenComparison('job')}
                      disabled={counts.jobs < 2}
                    >
                      <span className="material-icons">compare_arrows</span>
                      Compare Jobs
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonCartWidget;